import { useCallback, useMemo, useRef, useState } from "react";
import {
  UPLOAD_POLICIES,
  UPLOAD_PURPOSE_PREFLIGHT_ASSET,
  validateUploadMeta,
  type UploadValidationFailure,
} from "@nexus/contracts";
import { useAuth } from "@/features/auth/hooks/useAuth";
import {
  confirmPreflightUpload,
  presignPreflightUpload,
  putToSignedUrlWithProgress,
  stageMemoryPreflightUpload,
} from "../api/uploads.api";
import {
  usePreflightDraftStore,
  type PreflightFileMeta,
} from "../stores/preflight-draft.store";

/** Default bounded-concurrency cap for simultaneous uploads (KTD3). */
export const DEFAULT_UPLOAD_CONCURRENCY = 3;

const PREFLIGHT_POLICY = UPLOAD_POLICIES[UPLOAD_PURPOSE_PREFLIGHT_ASSET];

export type UploadItemState =
  | "queued"
  | "uploading"
  | "confirming"
  | "ready"
  | "failed"
  | "cancelled";

export interface UploadItem {
  /** Client-side id (stable across retries); distinct from the server `uploadId`. */
  id: string;
  file: File;
  state: UploadItemState;
  progress: number;
  uploadId?: string;
  error?: string;
  /** Overrides file.size for display when item was hydrated from store (stub file has 0 bytes). */
  sizeBytes?: number;
}

export interface UploadRejection {
  fileName: string;
  reason: UploadValidationFailure;
}

export interface UseUploadManagerResult {
  items: UploadItem[];
  rejections: UploadRejection[];
  addFiles: (files: FileList | File[]) => void;
  cancel: (id: string) => void;
  retry: (id: string) => void;
  removeItem: (id: string) => void;
  clearRejections: () => void;
  /** True while any item is still uploading/confirming (Analyze gating — Hub R8). */
  hasInFlight: boolean;
  /** Count of confirmed (ready) uploads. */
  readyCount: number;
  /** Abort in-flight uploads and clear the queue (e.g. after Analyze succeeds). */
  clearAll: () => void;
  /**
   * Populate the item list from persisted store state — called on mount when the
   * component was unmounted mid-session (e.g. the user navigated away and back).
   */
  hydrateFromStore: (
    uploadIds: string[],
    metas: Record<string, PreflightFileMeta>,
  ) => void;
}

function makeClientId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `upl_${Math.random().toString(36).slice(2)}_${Date.now()}`;
}

export function useUploadManager(
  concurrency: number = DEFAULT_UPLOAD_CONCURRENCY,
): UseUploadManagerResult {
  const [items, setItemsState] = useState<UploadItem[]>([]);
  const [rejections, setRejections] = useState<UploadRejection[]>([]);

  // itemsRef is the source of truth so the pump stays idempotent even when
  // React batches state updates — once an item is marked "uploading" in the
  // ref it can't be re-picked by a concurrent pump.
  const itemsRef = useRef<UploadItem[]>([]);
  const activeRef = useRef(0);
  const controllersRef = useRef<Map<string, AbortController>>(new Map());

  const commit = useCallback((next: UploadItem[]) => {
    itemsRef.current = next;
    setItemsState(next);
  }, []);

  const patch = useCallback(
    (id: string, changes: Partial<UploadItem>) => {
      commit(
        itemsRef.current.map((it) =>
          it.id === id ? { ...it, ...changes } : it,
        ),
      );
    },
    [commit],
  );

  // Kept in refs so cancel/retry/addFiles callbacks remain stable.
  const runItemRef = useRef<(item: UploadItem) => Promise<void>>(
    async () => {},
  );
  const pumpRef = useRef<() => void>(() => {});

  runItemRef.current = async (item: UploadItem) => {
    const controller = new AbortController();
    controllersRef.current.set(item.id, controller);

    const isItemActive = () =>
      itemsRef.current.some((it) => it.id === item.id);

    try {
      const tenantId = useAuth.getState().activeTenantId;
      if (!tenantId) throw new Error("No active tenant selected");

      const presign = await presignPreflightUpload({
        tenantId,
        contentType: item.file.type,
        sizeBytes: item.file.size,
        originalName: item.file.name,
      });
      if (controller.signal.aborted || !isItemActive()) {
        throw new DOMException("Aborted", "AbortError");
      }

      if (presign.requiresMemoryStage || presign.requiresServerStage) {
        // Dev/test staging path can't report progress — show indeterminate-full.
        await stageMemoryPreflightUpload({
          tenantId,
          uploadId: presign.uploadId,
          contentType: item.file.type,
          file: item.file,
        });
        if (!isItemActive()) throw new DOMException("Aborted", "AbortError");
        patch(item.id, { progress: 100 });
      } else {
        await putToSignedUrlWithProgress(
          presign.uploadUrl,
          item.file,
          presign.requiredHeaders,
          {
            onProgress: (percent) => patch(item.id, { progress: percent }),
            signal: controller.signal,
          },
        );
      }

      if (!isItemActive()) throw new DOMException("Aborted", "AbortError");

      patch(item.id, { state: "confirming" });
      const confirmed = await confirmPreflightUpload({
        tenantId,
        uploadId: presign.uploadId,
      });
      if (!isItemActive()) throw new DOMException("Aborted", "AbortError");

      patch(item.id, {
        state: "ready",
        uploadId: confirmed.uploadId,
        progress: 100,
      });
      usePreflightDraftStore.getState().addUpload(confirmed.uploadId, {
        name: item.file.name,
        sizeBytes: confirmed.sizeBytes ?? item.file.size,
        lastModified: item.file.lastModified,
        contentType: confirmed.contentType ?? item.file.type,
      });
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") {
        patch(item.id, { state: "cancelled" });
      } else {
        patch(item.id, {
          state: "failed",
          error: err instanceof Error ? err.message : "Upload failed",
        });
      }
    } finally {
      controllersRef.current.delete(item.id);
      activeRef.current = Math.max(0, activeRef.current - 1);
      pumpRef.current();
    }
  };

  pumpRef.current = () => {
    const slots = concurrency - activeRef.current;
    if (slots <= 0) return;
    const toStart = itemsRef.current
      .filter((it) => it.state === "queued")
      .slice(0, slots);
    if (toStart.length === 0) return;

    const startingIds = new Set(toStart.map((it) => it.id));
    activeRef.current += toStart.length;
    commit(
      itemsRef.current.map((it) =>
        startingIds.has(it.id)
          ? { ...it, state: "uploading", progress: 0, error: undefined }
          : it,
      ),
    );
    for (const item of toStart) void runItemRef.current(item);
  };

  const addFiles = useCallback(
    (files: FileList | File[]) => {
      const accepted: UploadItem[] = [];
      const rejected: UploadRejection[] = [];
      for (const file of Array.from(files)) {
        const result = validateUploadMeta(
          { contentType: file.type, sizeBytes: file.size },
          PREFLIGHT_POLICY,
        );
        if (result.ok) {
          accepted.push({
            id: makeClientId(),
            file,
            state: "queued",
            progress: 0,
          });
        } else {
          rejected.push({ fileName: file.name, reason: result.reason });
        }
      }
      if (accepted.length > 0) commit([...itemsRef.current, ...accepted]);
      if (rejected.length > 0) setRejections((prev) => [...prev, ...rejected]);
      pumpRef.current();
    },
    [commit],
  );

  const cancel = useCallback(
    (id: string) => {
      const controller = controllersRef.current.get(id);
      if (controller) {
        controller.abort();
      } else {
        patch(id, { state: "cancelled" });
      }
    },
    [patch],
  );

  const retry = useCallback(
    (id: string) => {
      patch(id, { state: "queued", progress: 0, error: undefined });
      pumpRef.current();
    },
    [patch],
  );

  const removeItem = useCallback(
    (id: string) => {
      const item = itemsRef.current.find((it) => it.id === id);
      if (item?.state === "uploading" || item?.state === "confirming") {
        controllersRef.current.get(id)?.abort();
      }
      if (item?.uploadId) {
        usePreflightDraftStore.getState().removeUpload(item.uploadId);
      }
      commit(itemsRef.current.filter((it) => it.id !== id));
    },
    [commit],
  );

  const clearRejections = useCallback(() => setRejections([]), []);

  const clearAll = useCallback(() => {
    for (const controller of controllersRef.current.values()) {
      controller.abort();
    }
    controllersRef.current.clear();
    activeRef.current = 0;
    commit([]);
  }, [commit]);

  const hydrateFromStore = useCallback(
    (uploadIds: string[], metas: Record<string, PreflightFileMeta>) => {
      const stubs: UploadItem[] = uploadIds.map((uploadId) => {
        const meta = metas[uploadId];
        const stubFile = new File([], meta?.name ?? uploadId, {
          type: meta?.contentType ?? "application/octet-stream",
          lastModified: meta?.lastModified ?? 0,
        });
        return {
          id: uploadId,
          file: stubFile,
          state: "ready" as const,
          progress: 100,
          uploadId,
          sizeBytes: meta?.sizeBytes,
        };
      });
      commit(stubs);
    },
    [commit],
  );

  const hasInFlight = useMemo(
    () =>
      items.some(
        (it) =>
          it.state === "queued" ||
          it.state === "uploading" ||
          it.state === "confirming",
      ),
    [items],
  );

  const readyCount = useMemo(
    () => items.filter((it) => it.state === "ready").length,
    [items],
  );

  return {
    items,
    rejections,
    addFiles,
    cancel,
    retry,
    removeItem,
    clearRejections,
    hasInFlight,
    readyCount,
    clearAll,
    hydrateFromStore,
  };
}
